import React from "react";
import {
  Box,
  Button,
  Collapse,
  Flex,
  Grid,
  Icon,
  IconButton,
  Text,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import CommonSpinner from "App/DMRC/CommonSpinner";
import { AppContext } from "AppProvider/AppContext";
import { useCallback, useContext, useEffect, useState } from "react";
import {
  FaCheckCircle,
  FaChevronDown,
  FaChevronUp,
  FaClock,
  FaEdit,
  FaTimesCircle,
} from "react-icons/fa";
import { appRequest } from "routes";
import { SELLER_CLIENT_URL } from "utils/env";
import { useHasPermission } from "../../common/PermissionsFunction/permissiosnFunction";

interface Props {
  data: any;
}

export default function IssueSellerStatus({ data }: Props): JSX.Element {
  const [sellerstatusData, setSellerStatusData] = useState<any>([]);
  const [openIndex, setOpenIndex] = useState<number | any>(null);
  const { toastNotify } = useContext(AppContext);
  const [loading, setLoading] = useState<boolean>(false);
  const toast = useToast();

  const getSellerIssueStatus = async () => {
    try {
      setLoading(true);
      const issueId = data?._id || "";
      const response: any = await appRequest(
        "issues",
        "getIssueStatusFromSeller",
        {
          issueId,
        }
      );
      if (response?.status === "success") {
        setSellerStatusData(response?.issue?.statusFlowSeller || []);
      }
    } catch (error: any) {
      console.error("Error fetching data:", error);
    } finally {
      await new Promise((r: any) => setTimeout(r, 1000));
      setLoading(false);
    }
  };

  const closeIssueWithSeller = async () => {
    let loadingToast: any;
    try {
      loadingToast = toast({
        title: "Processing",
        description: "Closing Issue with seller, please wait...",
        status: "loading",
        duration: null,
        isClosable: true,
        position: "top",
      });
      let issueId = data?._id;

      const response: any = await appRequest("issues", "closeIssueWithSeller", {
        issueId,
      });
      toast.close(loadingToast);
      if (response.status === "success") {
        toastNotify({
          title: "Success",
          message: "Issue closed with seller",
          type: "success",
        });
      }
    } catch (error: any) {
      toast.close(loadingToast);
      toastNotify({
        title: "Failed",
        message: "Something went wrong",
        type: "error",
      });
      console.log("error closing issue with seller", error);
    }
  };

  const handleToggle = (index: any) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  useEffect(() => {
    getSellerIssueStatus();
  }, [data?._id]);

  return (
    <>
      <Box overflowY={"auto"} className="customScrollBar" maxH={"38vh"} pb={1}>
        {loading ? (
          <Flex justifyContent="center" alignItems="center">
            <CommonSpinner />
          </Flex>
        ) : (
          <Grid templateColumns={{ md: "1fr 1fr" }} gap={4}>
            {sellerstatusData.map((item: any, index: number) => (
              <Box
                key={`status-${index}`}
                p={{ base: 2, md: 3 }}
                borderWidth={1}
                borderRadius="md"
                bg={
                  item.status === "PROCESSING"
                    ? "green.50"
                    : item.status === "ESCALATED"
                    ? "red.50"
                    : item.status === "CLOSED"
                    ? "gray.50"
                    : "blue.50"
                }
                _hover={{
                  bg:
                    item.status === "PROCESSING"
                      ? "green.50"
                      : item.status === "ESCALATED"
                      ? "red.50"
                      : item.status === "CLOSED"
                      ? "gray.50"
                      : "blue.50",
                }}
                transition="background-color 0.3s"
                onClick={() => handleToggle(`status-${index}`)}
                cursor={"pointer"}
                h={"fit-content"}
              >
                <Flex justifyContent="space-between" alignItems="center">
                  <Flex alignItems="center">
                    <Icon
                      as={
                        item.status === "OPEN"
                          ? FaEdit
                          : item.status === "PROCESSING"
                          ? FaClock
                          : item.status === "ESCALATED"
                          ? FaTimesCircle
                          : item.status === "CLOSED"
                          ? FaCheckCircle
                          : undefined
                      }
                      color={
                        item.status === "PROCESSING"
                          ? "green.500"
                          : item.status === "ESCALATED"
                          ? "red.500"
                          : item.status === "CLOSED"
                          ? "gray.500"
                          : "blue.500"
                      }
                      mr={2}
                    />
                    <Text fontWeight="bold" color="blue.800">
                      {item?.status}
                    </Text>
                  </Flex>
                  <Flex alignItems="center">
                    <Text fontSize="sm" color="gray.600" mr={2}>
                      {new Date(item?.updatedAt).toLocaleString()}
                    </Text>
                  </Flex>
                </Flex>
                <Flex justifyContent="space-between" alignItems="center">
                  {/* <Text fontSize="sm" color="gray.600">
                          {action?.short_desc}
                        </Text> */}
                  <IconButton
                    bg={"transparent"}
                    _hover={{ bg: "blackAlpha.200" }}
                    aria-label={
                      openIndex === `status-${index}`
                        ? "Hide Details"
                        : "Show Details"
                    }
                    icon={
                      openIndex === `status-${index}` ? (
                        <FaChevronUp />
                      ) : (
                        <FaChevronDown />
                      )
                    }
                    size="sm"
                    onClick={() => handleToggle(`status-${index}`)}
                  />
                </Flex>
                <Collapse in={openIndex === `status-${index}`}>
                  <Box
                    mt={2}
                    p={2}
                    borderWidth={1}
                    borderRadius="md"
                    bg="white"
                  >
                    <Text fontSize="xs" color="gray.500">
                      <Tooltip
                        label={item?.updatedBy?.person?.name}
                        aria-label="name"
                      >
                        <span>Updated by: {item?.updatedBy?.person?.name}</span>
                      </Tooltip>
                      <br />
                      <Tooltip
                        label={item?.updatedBy?.contact?.email}
                        aria-label="email"
                      >
                        <span>Email: {item?.updatedBy?.contact?.email}</span>
                      </Tooltip>
                      <br />
                      <Tooltip
                        label={item?.updatedBy?.contact?.phone}
                        aria-label="Phone"
                      >
                        <span>Phone: {item?.updatedBy?.contact?.phone}</span>
                      </Tooltip>
                      <br />
                      <Tooltip
                        label={item?.updatedBy?.org?.name}
                        aria-label="Organization"
                      >
                        <span>Organization: {item?.updatedBy?.org?.name}</span>
                      </Tooltip>
                    </Text>
                  </Box>
                </Collapse>
              </Box>
            ))}
          </Grid>
        )}
        {/* <Flex justify={"end"} w={"100%"} align={"center"} gap={2}>
          {useHasPermission("canUpdateIssue") && (
            <Button
              colorScheme="red"
              onClick={() => closeIssueWithSeller()}
              boxShadow="md"
              size="sm"
              _hover={{ boxShadow: "xl" }}
            >
              Close issue with Seller
            </Button>
          )}
        </Flex> */}
      </Box>
    </>
  );
}
